import React from 'react';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';
import { ChevronRight, TrendingUp, TrendingDown } from 'lucide-react';

function PillarCard({ name, score, delta, icon, color, trendData, onDrillDown }) {
  // Generate sample sparkline data if no trend data provided
  const sparklineData = trendData?.length > 0 ? trendData : [
    { value: score * 0.85 },
    { value: score * 0.9 },
    { value: score * 0.88 },
    { value: score * 0.95 },
    { value: score * 0.92 },
    { value: score }
  ];

  const getSparklineColor = (colorClass) => {
    const colorMap = {
      'bg-blue-500': '#3b82f6',
      'bg-purple-500': '#a855f7',
      'bg-green-500': '#10b981'
    };
    return colorMap[colorClass] || '#9333ea';
  };

  const handleClick = () => {
    if (onDrillDown) {
      onDrillDown(name.toLowerCase());
    }
  };

  return (
    <div 
      className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group" 
      data-testid={`pillar-card-${name.toLowerCase()}`}
      onClick={handleClick}
    >
      {/* Header */}
      <div className={`${color} p-4 text-white`}>
        <div className="flex items-center justify-between">
          <div className="text-3xl">{icon}</div>
          <div className="text-right">
            <div className="text-3xl font-bold" data-testid={`${name.toLowerCase()}-score`}>
              {score.toFixed(1)}
            </div>
            <div className="text-sm opacity-90">out of 100</div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold text-gray-900">{name}</h3>
          <ChevronRight 
            className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-transform group-hover:translate-x-1" 
          />
        </div>
        
        {delta !== undefined && delta !== null && (
          <div className="flex items-center gap-2 mb-3">
            {delta >= 0 ? (
              <TrendingUp className="w-4 h-4 text-green-600" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-600" />
            )}
            <span className={`font-semibold ${
              delta >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {delta > 0 ? '+' : ''}{delta.toFixed(1)}
            </span>
            <span className="text-sm text-gray-600">vs last period</span>
          </div>
        )}

        {/* Sparkline */}
        <div className="mt-3 h-12" style={{ minWidth: 100 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={100}>
            <LineChart data={sparklineData}>
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: 'none', 
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                }}
                formatter={(value) => [value.toFixed(1), 'Score']}
              />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke={getSparklineColor(color)}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Click for details hint */}
        <div className="mt-3 text-center">
          <span className="text-xs text-gray-400 group-hover:text-gray-600 transition">
            Click to view details
          </span>
        </div>
      </div>
    </div>
  );
}

export default PillarCard;
